import { motion } from "framer-motion";
import { type CSSProperties, type RefObject, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { AffairePanel } from "@/features/procedures/components/affaire-panel";
import { useRailMotion } from "@/features/procedures/lib/rail-motion";

/** Wide enough for a numéro RG and a court name without wrapping. */
const PANEL_WIDTH = 384;
const GUTTER = 8;

interface AffaireFlyoutProps {
  procedureId: string;
  /** Preview pane the flyout docks against — it opens just outside its left edge. */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/**
 * The affaire of the previewed claim, docked to the left of the preview pane and
 * floating over the table behind it. Portalled to the body because every host of
 * the preview pane clips its overflow, and positioned off the pane's own rect so
 * the resizable claims pane keeps the flyout glued to its edge.
 *
 * It travels from the left where the procedures rail travels from the right: the
 * panel comes from the side of the screen it is docked on, either way.
 */
export function AffaireFlyout({ procedureId, anchorRef, onClose }: AffaireFlyoutProps) {
  const { hidden, visible, transition } = useRailMotion({ travel: -16 });
  const [box, setBox] = useState<{ right: number; top: number; maxHeight: number } | null>(null);

  useEffect(() => {
    const element = anchorRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setBox({
        right: window.innerWidth - rect.left + GUTTER,
        top: rect.top + GUTTER,
        maxHeight: rect.height - GUTTER * 2,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    // Capture phase: the pane sits inside scrollable hosts that never bubble.
    window.addEventListener("scroll", measure, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [anchorRef]);

  if (!box) {
    return null;
  }

  return createPortal(
    <motion.aside
      initial={hidden}
      animate={visible}
      exit={hidden}
      transition={transition}
      // AffairePanel caps itself against the viewport; the child selector hands it
      // the pane's height instead, so the flyout never outruns what it is docked to.
      // A pixel value, not max-h-full: a percentage max-height resolves against a
      // definite parent height, and this wrapper is sized by its content — it would
      // collapse to `none` and take the panel's own cap (and its scroll) with it.
      className="fixed z-40 [&>div]:max-h-[var(--flyout-max-height)]"
      style={
        {
          right: box.right,
          top: box.top,
          width: PANEL_WIDTH,
          "--flyout-max-height": `${box.maxHeight}px`,
        } as CSSProperties
      }
    >
      <AffairePanel procedureId={procedureId} hint={null} onClose={onClose} />
    </motion.aside>,
    document.body,
  );
}
